import { useEffect } from "react";
import queryString from "query-string";
import { useLocation } from "@gatsbyjs/reach-router";
import { useHydration } from "@puregym/react-hooks";
import {
  Button,
  BUTTON_VARIANTS,
  SearchForm as PureSearchForm,
} from "@puregym/ui";
import labels from "../labels";
import asyncActions from "../../asyncActions";
import * as ACTIONS from "../../constants/actions";
import { useGymSearch } from "../../context";
import {
  SearchFormWrapper,
  GymResultsAndReset,
  SearchFormHeader,
  SearchHeading,
} from "../../styles/GymSearchForm";
import { DisplayType } from "../../constants/ui";
import SearchHint from "../gymIndexPage/search/searchHint";
import SearchResultsAlert from "../gymIndexPage/search/searchResultsAlert";
import SearchModal from "../gymIndexPage/search/searchModal";
import ToggleButton from "../gymIndexPage/toggleButton";

const SearchForm = ({
  displayType,
  toggleDisplayType,
}: {
  displayType: DisplayType;
  toggleDisplayType: (displayType: DisplayType) => void;
}) => {
  const [state, dispatch] = useGymSearch();
  const { search } = useLocation();
  const enabled = useHydration();
  const { displaySearchError, filteredGyms, gyms, isSearching, searchTerm } =
    state;
  const resultsFiltered =
    filteredGyms.length < gyms.length && filteredGyms.length > 0;

  /**  If there's a search term in query params, try and get a location  */
  useEffect(() => {
    const searchTerm = queryString.parse(search).search;

    if (searchTerm) {
      asyncActions.getLocationGeometry(dispatch, searchTerm);
      dispatch({
        type: ACTIONS.UPDATE_SEARCH_TERM,
        payload: searchTerm,
      });
    }
  }, [dispatch, search]);

  return (
    <SearchFormWrapper>
      <SearchFormHeader>
        <SearchHeading>{labels.gymFinder.heading}</SearchHeading>
        <ToggleButton
          toggleDisplayType={toggleDisplayType}
          displayType={displayType}
        />
      </SearchFormHeader>
      <PureSearchForm
        disabled={!enabled}
        buttonText={labels.gymFinder.search}
        buttonProps={{
          "data-testid": "searchButton",
          disabled: !enabled || (enabled && isSearching),
        }}
        geoLocationButtonProps={{
          disabled: !enabled || (enabled && isSearching),
        }}
        geoLocationTitleText={labels.gymFinder.currentLocation}
        hideLabel={true}
        id="gymSearchForm"
        inputProps={{
          autoComplete: "off",
          autoFocus: true,
          "data-testid": "searchLocation",
          disabled: !enabled || (enabled && isSearching),
          value: searchTerm,
        }}
        label={labels.gymFinder.location}
        onChange={(e) =>
          dispatch({
            type: ACTIONS.UPDATE_SEARCH_TERM,
            payload: e.target.value,
          })
        }
        onGeoLocationClick={() => asyncActions.getGeolocation(dispatch)}
        onSubmit={(e) => {
          e.preventDefault();
          asyncActions.getLocationGeometry(dispatch, searchTerm);
          dispatch({
            type: ACTIONS.UPDATE_SEARCH_TERM,
            payload: "",
          });
        }}
        placeholder={labels.gymFinder.location}
        showGeoLocation={true}
      />
      {displaySearchError && <SearchResultsAlert />}
      {resultsFiltered && (
        <GymResultsAndReset>
          <SearchHint data-testid="gymCountHint" />
          <Button
            data-testid="resetButton"
            onClick={() =>
              dispatch({
                type: ACTIONS.RESET_GYMS,
              })
            }
            variant={BUTTON_VARIANTS.OUTLINE}
          >
            {labels.gymFinder.backToAllGyms}
          </Button>
        </GymResultsAndReset>
      )}
      <SearchModal />
    </SearchFormWrapper>
  );
};

export default SearchForm;
